import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { Alert, ContentCard, Row, Button } from "@narmi/design_system";
import ApiHttp from "byzantine/src/ApiHttp";
import HeaderWithSteps from "../../HeaderWithSteps";
import Container from "../../Container";
import { useACHPassthroughImportContext } from "../ACHPassthroughImportContext";
import {
  formatCurrencyFromCents,
  formatDate,
  formatEffectiveDateRange,
} from "./passthroughFormat";
import styles from "./PassthroughReviewScreen.module.scss";

type L10n = ReturnType<typeof useLocalization>["l10n"];

const formatListWithMore = (values: string[], l10n: L10n, visibleCount = 2): string => {
  if (values.length <= visibleCount) return values.join(", ");
  const remaining = values.length - visibleCount;
  const more = l10n.getString("ach-passthrough-validation-more-count", { count: remaining });
  return `${values.slice(0, visibleCount).join(", ")}, ${more}`;
};

const getSubmitErrorMessage = (error: unknown, fallback: string): string => {
  const apiErrorData = (
    error as { response?: { data?: Partial<API.ACHPassthrough.SubmitErrorResponse> } }
  )?.response?.data;
  if (Array.isArray(apiErrorData?.errors) && apiErrorData.errors.length > 0) {
    return apiErrorData.errors.map((entry) => entry.message).join(" ");
  }
  if (typeof apiErrorData?.message === "string") return apiErrorData.message;
  // With rawResponse, anything else is a transport message, e.g. "Request failed
  // with status code 500", which isn't user-facing copy.
  return fallback;
};

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <div className={styles.detailRow}>
    <span className={`${styles.detailLabel} fontSize--s fontColor--secondary`}>{label}</span>
    <span className={`${styles.detailValue} fontSize--s fontWeight--semibold`}>{value}</span>
  </div>
);

const PassthroughReviewScreen = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { validateResponse, fileName, title, internalNote, setSubmittedState } =
    useACHPassthroughImportContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (
    !validateResponse ||
    !validateResponse.file_summary ||
    !validateResponse.submission ||
    !fileName
  ) {
    return <Navigate to="../.." replace />;
  }

  const { file_summary: fileSummary, submission: submissionPreview } = validateResponse;
  const netAmountCents =
    fileSummary.payment_total_debit_amount - fileSummary.payment_total_credit_amount;

  const handleBack = () => {
    navigate("../additional-details");
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!title.trim()) {
      setSubmitError(l10n.getString("wire-payment-details-error-required-title"));
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const response = await ApiHttp.fetch<API.NachaPassthrough.Submission>(
        `ach_nacha_passthrough/${submissionPreview.id}/submit/`,
        { method: "POST", rawResponse: true },
        {
          title,
          internal_note: internalNote,
          original_file_name: fileName,
        } as API.ACHPassthrough.SubmitRequest
      );

      const { data: submittedSubmission } = response as unknown as {
        data: API.NachaPassthrough.Submission;
      };
      setSubmittedState(submittedSubmission.state);
      navigate("../receipt", { replace: true });
    } catch (error) {
      setSubmitError(
        getSubmitErrorMessage(error, l10n.getString("ach-passthrough-review-submit-error"))
      );
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="640px">
      <HeaderWithSteps
        useDisplayFont
        headerText={l10n.getString("ach-passthrough-review-title")}
      />

      <div className="margin--top--l">
        <ContentCard kind="bordered" paddingSize="l" radiusSize="m">
          <div className={styles.netTotal}>
            <span className="fontSize--xs fontColor--secondary">
              {l10n.getString("ach-passthrough-review-net-total-label")}
            </span>
            <span className="fontSize--l fontWeight--bold">
              {formatCurrencyFromCents(netAmountCents)}
            </span>
          </div>
          <div className={styles.directionRow}>
            <span className="fontSize--s fontColor--secondary">
              {l10n.getString("ach-passthrough-review-sending")}
            </span>
            <span className="fontSize--s fontWeight--semibold">
              {l10n.getString("ach-passthrough-review-direction-detail", {
                amount: formatCurrencyFromCents(fileSummary.payment_total_credit_amount),
                count: fileSummary.payment_credit_count,
              })}
            </span>
          </div>
          <div className={styles.directionRow}>
            <span className="fontSize--s fontColor--secondary">
              {l10n.getString("ach-passthrough-review-collecting")}
            </span>
            <span className="fontSize--s fontWeight--semibold">
              {l10n.getString("ach-passthrough-review-direction-detail", {
                amount: formatCurrencyFromCents(fileSummary.payment_total_debit_amount),
                count: fileSummary.payment_debit_count,
              })}
            </span>
          </div>
        </ContentCard>
      </div>

      <div className="margin--top--l">
        <ContentCard kind="bordered" paddingSize="l" radiusSize="m">
          <div className="fontSize--xs fontWeight--bold fontColor--secondary margin--bottom--s">
            {l10n.getString("ach-passthrough-review-payment-details-heading")}
          </div>
          <DetailRow label={l10n.getString("bulk-payment-title")} value={title} />
          <DetailRow
            label={l10n.getString("ach-passthrough-validation-check-company-id")}
            value={formatListWithMore(fileSummary.company_ids, l10n)}
          />
          <DetailRow
            label={l10n.getString("ach-passthrough-validation-check-sec-codes")}
            value={fileSummary.sec_codes.join(", ")}
          />
          <DetailRow
            label={l10n.getString("ach-passthrough-validation-check-origin-name")}
            value={fileSummary.origin_name ?? ""}
          />
          <DetailRow
            label={l10n.getString("ach-passthrough-validation-check-origin-routing")}
            value={fileSummary.origin_routing_number ?? ""}
          />
          <DetailRow
            label={l10n.getString("ach-passthrough-review-processing-date")}
            value={formatDate(submissionPreview.processing_date)}
          />
          <DetailRow
            label={l10n.getString("ach-passthrough-validation-check-effective-date")}
            value={formatEffectiveDateRange(fileSummary) ?? ""}
          />
          <DetailRow
            label={l10n.getString("ach-passthrough-review-internal-note-label")}
            value={internalNote}
          />
        </ContentCard>
      </div>

      {submitError && (
        <div className="margin--top--l">
          <Alert isActive kind="error" isDismissable={false}>
            {submitError}
          </Alert>
        </div>
      )}

      <div className="margin--bottom--xl margin--top--xl">
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <Button
              kind="plain"
              label={l10n.getString("button-back")}
              onClick={handleBack}
              disabled={isSubmitting}
            />
          </Row.Item>
          <Row.Item shrink>
            <Button
              label={l10n.getString("button-submit")}
              onClick={handleSubmit}
              isLoading={isSubmitting}
              testId="passthrough-review-submit"
            />
          </Row.Item>
        </Row>
      </div>
    </Container>
  );
};

export default PassthroughReviewScreen;
